import type { AnchorHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Body, Prose } from '@leanin/ui/server';
import type { MentionView } from '@leanin/shared';
import { mentionTokens } from '@leanin/shared';
import { ContentAnchor } from '@/components/external-link';
import styles from './PostBodyRenderer.module.css';
import { linkifyUrls } from './linkify-urls';
import { MEMBER_LINK_PREFIX, remarkPostMentions } from './remark-post-mentions';

// Renders post/comment bodies. Two formats, selected by the row's
// `bodyFormat`:
//
// - 'v2': the GFM-subset dialect (**bold**, *italic*, <u>underline</u>,
//   lists, [text](url) links + bare-URL autolinks, @handle mentions,
//   remark-breaks newline semantics), rendered through the Prose primitive's
//   `simple` variant — the sanitize schema is the security boundary, and
//   block elements the dialect excludes are stripped.
// - 'v1' (default): the legacy 3-mark pseudo-markdown, parsed with the
//   split-chain below. Built entirely from React elements — no
//   dangerouslySetInnerHTML, no external parser, no XSS surface. Unmatched
//   or unclosed markers are treated as literal characters. Keep the subset
//   in lockstep with components/editor/markdown.ts and
//   normalize-legacy-body.ts.


type Props = {
  readonly children: string;
  // Matches the per-post/comment `bodyFormat` field the API returns; typed
  // locally until the generated API types carry it.
  readonly bodyFormat?: 'v1' | 'v2';
  readonly mentions?: ReadonlyArray<MentionView>;
  readonly style?: CSSProperties;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly as?: React.ElementType;
};

type ResolvedMention = { userId: string; name: string };

export function PostBodyRenderer({ children, bodyFormat = 'v1', mentions, style, size, as }: Props) {
  if (bodyFormat === 'v2') {
    return (
      <Body
        size={size}
        as={as ?? 'div'}
        style={{ overflowWrap: 'break-word', ...style }}
      >
        <Prose
          source={children}
          variant="simple"
          typography="inherit"
          breaks
          allowRawHtml
          remarkPlugins={[remarkPostMentions(mentions ?? [])]}
          extendSanitizeSchema={(schema) => ({
            ...schema,
            tagNames: [...(schema.tagNames ?? []), 'u'],
            protocols: { ...schema.protocols, href: ['http', 'https'] },
          })}
          components={{ a: PostBodyAnchor }}
        />
      </Body>
    );
  }

  const mentionMap = new Map<string, ResolvedMention>(
    mentions?.map((m) => [m.handle, { userId: m.userId, name: m.name }]) ?? [],
  );
  // Cap newline runs at one blank line. Two jobs: (1) bodies stored before
  // the htmlToMarkdown double-prefix fix carry 2–3 newlines per typed Enter,
  // which pre-wrap would otherwise render as giant gaps; (2) it bounds
  // pasted/typed vertical whitespace the same way LinkedIn-style feeds do.
  const text = children.replace(/\n{3,}/g, '\n\n');
  return (
    <Body
      size={size}
      // pre-wrap: author newlines are meaningful — without it multi-paragraph
      // bodies collapse into one run of text.
      style={{ overflowWrap: 'break-word', whiteSpace: 'pre-wrap', ...style }}
      {...(as ? { as } : {})}
    >
      {parseBody(text, mentionMap)}
    </Body>
  );
}

// Anchor renderer for the v2 Prose path. Mention link nodes (produced by
// remarkPostMentions) carry a `/members/<userId>` href and render as router
// links with the mention treatment; every other link defers to ContentAnchor
// (internal Connect links → same-tab SPA nav, external → confirm interstitial,
// a sanitizer-stripped href → plain text).
function PostBodyAnchor({
  href,
  children,
  // react-markdown passes the hast node to component overrides; it must not
  // reach the DOM element.
  node: _node,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  if (href !== undefined && href.startsWith(MEMBER_LINK_PREFIX)) {
    return (
      <Link
        to="/members/$id"
        params={{ id: href.slice(MEMBER_LINK_PREFIX.length) }}
        className={styles.mention}
      >
        {children}
      </Link>
    );
  }
  return (
    <ContentAnchor href={href} {...rest}>
      {children}
    </ContentAnchor>
  );
}

function parseBody(text: string, mentionMap: Map<string, ResolvedMention>): ReactNode {
  // Split on ** first (bold), then *(italic), __ (underline), and @mentions
  // within each segment.
  const boldParts = text.split('**');
  if (boldParts.length === 1) return applyItalic(text, '0', mentionMap);
  return boldParts.map((part, i) =>
    i % 2 === 0 ? (
      applyItalic(part, `p${i}`, mentionMap)
    ) : (
      <strong key={`b${i}`}>{applyItalic(part, `s${i}`, mentionMap)}</strong>
    ),
  );
}

function applyItalic(text: string, keyPrefix: string, mentionMap: Map<string, ResolvedMention>): ReactNode {
  const parts = text.split('*');
  if (parts.length === 1) return applyUnderline(text, keyPrefix, mentionMap);
  return parts.map((part, i) =>
    i % 2 === 0
      ? applyUnderline(part, `${keyPrefix}i${i}`, mentionMap)
      : <em key={`${keyPrefix}em${i}`}>{applyUnderline(part, `${keyPrefix}is${i}`, mentionMap)}</em>,
  );
}

function applyUnderline(text: string, keyPrefix: string, mentionMap: Map<string, ResolvedMention>): ReactNode {
  const parts = text.split('__');
  if (parts.length === 1) return applyMentions(text, keyPrefix, mentionMap);
  return parts.map((part, i) =>
    i % 2 === 0
      ? applyMentions(part, `${keyPrefix}p${i}`, mentionMap)
      : <u key={`${keyPrefix}u${i}`}>{applyMentions(part, `${keyPrefix}us${i}`, mentionMap)}</u>,
  );
}

function applyMentions(text: string, keyPrefix: string, mentionMap: Map<string, ResolvedMention>): ReactNode {
  if (!text.includes('@')) return linkifyUrls(text, keyPrefix);
  const segments: ReactNode[] = [];
  let last = 0;
  for (const token of mentionTokens(text)) {
    if (token.index > last) segments.push(linkifyUrls(text.slice(last, token.index), `${keyPrefix}pre${String(token.index)}`));
    const mention = mentionMap.get(token.handle);
    if (mention !== undefined) {
      segments.push(
        <Link
          key={`${keyPrefix}m${String(token.index)}`}
          to="/members/$id"
          params={{ id: mention.userId }}
          className={styles.mention}
        >
          {mention.name}
        </Link>,
      );
    } else {
      // Handle not in the resolved map (user deleted, unknown) — render as plain text.
      segments.push(text.slice(token.index, token.index + token.length));
    }
    last = token.index + token.length;
  }
  if (last < text.length) segments.push(linkifyUrls(text.slice(last), `${keyPrefix}post`));
  return segments.length === 1 ? segments[0] : segments;
}
